"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Tabs = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _Tab = require("../Tab/Tab");
var _TabList = require("../TabList/TabList");
var _TabPanel = require("../TabPanel/TabPanel");
var _TabPanels = require("../TabPanels/TabPanels");
var _Tabs = require("./Tabs.context");
var _Tabs2 = require("./Tabs.styles");
var _excluded = ["appearance", "children", "defaultSelected", "fluid", "manualActivation", "onSelected", "selected"];
/**
 * A `Tabs` component acts as the container for `Tabs.List` and `Tabs.Panels`.
 *
 * If you aren't using `Tabs.Panels`, you can use `Tabs.List` without this `Tabs` parent,
 * if you prefer.
 *
 * @subcomponents ['Tabs.List', 'Tabs.Panel', 'Tabs.Panels', 'Tabs.Tab']
 * @since 8.10.0
 */
var Tabs = /*#__PURE__*/(0, _react.forwardRef)(function (_ref, forwardedRef) {
  var appearance = _ref.appearance,
    children = _ref.children,
    defaultSelected = _ref.defaultSelected,
    fluid = _ref.fluid,
    manualActivation = _ref.manualActivation,
    onSelected = _ref.onSelected,
    selected = _ref.selected,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  var _useState = (0, _react.useState)(defaultSelected),
    _useState2 = (0, _slicedToArray2.default)(_useState, 2),
    selectedState = _useState2[0],
    setSelectedState = _useState2[1];
  return /*#__PURE__*/_react.default.createElement(_Tabs.TabsContext.Provider, {
    value: {
      appearance: appearance,
      fluid: fluid,
      manualActivation: manualActivation,
      onSelected: onSelected,
      selected: selected,
      selectedState: selectedState,
      setSelectedState: setSelectedState
    }
  }, /*#__PURE__*/_react.default.createElement(_Tabs2.StyledTabs, (0, _extends2.default)({
    appearance: appearance,
    fluid: fluid,
    ref: forwardedRef
  }, rest), children));
});
exports.Tabs = Tabs;
Tabs.displayName = 'Tabs';
Tabs.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The style of `Tabs.Tab` to display. Overrides `appearance` set on `Tabs.List`.
   */
  appearance: _propTypes.default.oneOf(Object.values(_constants.TABS_APPEARANCES)),
  /**
   * `Tabs` expects `Tabs.List` and `Tabs.Panels` as direct children.
   */
  children: _propTypes.default.node.isRequired,
  /**
   * The id of the `Tabs.Tab` to select by default. When set, `Tabs` will behave as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * `Tabs` must be set with either `selected` or `defaultSelected`, but not both.
   *
   * Overrides `defaultSelected` set on `Tabs.List`.
   */
  defaultSelected: _propTypes.default.string,
  /**
   * When `true`, the `Tabs.List` stretches to fill its container and tries to maintain
   * equal tab widths. Overrides `fluid` set on `Tabs.List`.
   */
  fluid: _propTypes.default.bool,
  /**
   * Describes the behavior when navigating the `Tabs.List`
   * with arrow keys. By default, when a new `Tabs.Tab` is given
   * focus, its corresponding `Tabs.Panel` will
   * display automatically. When `manualActivation` is set to "true", the user must press
   * the Spacebar or Enter key before the panel is displayed.
   *
   * In circumstances where content can be displayed instantly (i.e., all `Tabs.Panel` content is
   * present in the DOM), use the default behavior. If changing panels requires first loading the
   * content, set `manualActivation` to "true".
   * https://www.w3.org/TR/wai-aria-practices-1.2/#kbd_selection_follows_focus
   */
  manualActivation: _propTypes.default.bool,
  /**
   * Callback for when a new tab is selected.
   * Passes the id of the selected `Tabs.Tab`.
   */
  onSelected: _propTypes.default.func,
  /**
   * The id of the selected `Tabs.Tab`. When used, `Tabs` will
   * behave as a [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * `Tabs` must be set with either `selected` or `defaultSelected`, but not both.
   *
   * Overrides `selected` set on `Tabs.List`.
   */
  selected: _propTypes.default.string
} : {};
Tabs.defaultProps = {
  appearance: _constants.TABS_APPEARANCES.default
};
Tabs.List = _TabList.TabList;
Tabs.Panel = _TabPanel.TabPanel;
Tabs.Panels = _TabPanels.TabPanels;
Tabs.Tab = _Tab.Tab;